import {WithAPI} from "../../../../api/temperature";
import {Dialog, DialogClose, DialogContent, DialogOptions} from "../../../generic/dialog";
import {Sensor} from "../../../../api/sensor";
import {useFetchAndRender} from "../../../fetchAndRender";
import {useState} from "preact/hooks";
import {useAlert} from "../../../generic/alert";
import {Card} from "../../../generic/card";
import {FormFooter, FormGroup} from "../../../generic/form";
import RangeSlider from "../../../generic/slider";
import DatePicker from "react-datepicker";
import {Button} from "../../../generic/button";
import {useFlash} from "../../../generic/flash";

export function TrimSensorDialog({api, sensor, ...props}: WithAPI & DialogOptions & { sensor: Sensor }) {
    const [data] = useFetchAndRender(async () => {
        return (await api.sensor.get({
            attributes: ["since", "last"],
            filter: {
                active: undefined,
                ids: [sensor.id]
            }
        })).sensors[0];
    }, {interval: 0});

    if (!data) {
        return null;
    }

    const min = new Date(data.since ?? 0);
    const max = (data.last?.date) ? new Date(data.last.date) : new Date();

    const [range, setRange] = useState<[number, number]>([min.getTime(), max.getTime()]);

    const {showConfirm} = useAlert();
    const {success} = useFlash();

    return <Dialog {...props}>
        <DialogContent>
            <Card title={`Trim sensor ${sensor.name} by time`}>
                <form onSubmit={(e) => {
                    e.preventDefault();

                    const trimFrom = range[0] == min.getTime() ? null : new Date(range[0]);
                    const trimTo = range[1] == max.getTime() ? null : new Date(range[1]);

                    showConfirm(
                        "Trim sensor data?",
                        <>
                            <p>{`Do you really want to trim sensor data from ${trimFrom ? trimFrom : "beginning"} to ${trimTo ? trimTo : "now"}?`}</p>
                            <p class={"text-danger"}><b>This operation cannot be undone!</b></p>
                        </>
                    ).then((resolution) => {
                        if (resolution == "yes") {
                            api.sensor.trim(
                                sensor,
                                new Date(range[0]),
                                new Date(range[1])
                            ).then(() => {
                                success(`Sensor ${sensor.name} has been trimmed to range from ${trimFrom ? trimFrom : "beginning"} to ${trimTo ? trimTo : "now"}`);
                                props.onOpenChange(false);
                            })
                        }
                    });
                }}>
                    <FormGroup>
                        <label for="input--trim">Trim sensor data to period:</label>
                        <RangeSlider
                            min={min.getTime()}
                            max={max.getTime()}
                            value={range}
                            onInput={(value: [number, number]) => {
                                setRange(value);
                            }}
                        />
                        <div style={{display: "flex", justifyContent: "space-between"}}>
                            <DatePicker
                                selected={new Date(range[0])}
                                onChange={(value) => {
                                    setRange([value ? value.getTime() : range[0], range[1]]);
                                }}
                                withPortal={true}
                                title="Start of range"
                                showTimeSelect={true}
                                timeIntervals={1}
                                showMonthDropdown={true}
                                showYearDropdown={true}
                                minDate={min}
                                maxDate={new Date(range[1])}
                                dateFormat="Pp"
                            />
                            <DatePicker
                                selected={new Date(range[1])}
                                onChange={(value) => {
                                    setRange([range[0], value ? value.getTime() : range[1]]);
                                }}
                                withPortal={true}
                                title="End of range"
                                showTimeSelect={true}
                                timeIntervals={1}
                                showMonthDropdown={true}
                                showYearDropdown={true}
                                minDate={new Date(range[0])}
                                maxDate={max}
                                dateFormat="Pp"
                            />
                        </div>
                    </FormGroup>

                    <FormFooter>
                        <Button type="submit">Trim</Button>
                        <DialogClose>Cancel</DialogClose>
                    </FormFooter>
                </form>
            </Card>
        </DialogContent>
    </Dialog>
}
